import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=logged out, object=user
  const [token, setToken] = useState(localStorage.getItem("bsg_token"));

  useEffect(() => {
    if (!token) {
      setUser(false);
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const tryMe = () => {
      api
        .get("/auth/me")
        .then((r) => { if (!cancelled) setUser(r.data); })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 401) {
            // Genuine auth failure (invalid/expired token) → log out.
            localStorage.removeItem("bsg_token");
            setToken(null);
            setUser(false);
          } else if (attempt < 4) {
            // Transient error (network blip, redeploy 502, timeout) → retry, KEEP the session.
            attempt += 1;
            setTimeout(tryMe, 1500 * attempt);
          } else {
            // Give up retrying but DO NOT log the user out; keep the token for the next load.
            setUser((u) => (u && typeof u === "object" ? u : false));
          }
        });
    };
    tryMe();
    return () => { cancelled = true; };
  }, [token]);

  const login = (tok, u) => {
    localStorage.setItem("bsg_token", tok);
    setToken(tok);
    setUser(u);
  };

  const logout = () => {
    api.put("/driver/availability", { available: false }).catch(() => {});
    localStorage.removeItem("bsg_token");
    setToken(null);
    setUser(false);
  };

  const refresh = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  const switchRole = async (role) => {
    const { data } = await api.post("/auth/switch-role", { role });
    login(data.token, data.user);
    return data.user;
  };

  const addRole = async (role) => {
    const { data } = await api.post("/auth/add-role", { role });
    login(data.token, data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, switchRole, addRole, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
